import type { Entry, EntrySkeletonType } from "contentful"
import { reportErrorMessage } from "#/utils/ErrorReporting"
import { AttributeConditionType, CountryConditionType } from "#/services/contentful/conditional-gate"

// The CDA caps `include` at 10, so on a deep tree (the DBG) an entry that is only reachable through its deepest path
// comes back as a bare link. `withoutUnresolvableLinks` then silently drops it, and when that entry is a condition the
// gated content shows to every member. The same happens one level down when a condition resolves at the cap but its
// attribute/country links don't (stripped, they leave an empty list that passes the gate). This refetches both kinds by
// id, hides (fails closed) anything whose condition is still incomplete, and strips any remaining unresolved links to
// match `withoutUnresolvableLinks`.

type LinkStub = { sys: { type: "Link"; linkType: string; id: string } }
type UnresolvedCondition = { conditions: unknown[]; index: number; id: string }

export type FetchEntriesByIds = (ids: string[]) => Promise<Entry<EntrySkeletonType, undefined, string>[]>

const FETCH_CHUNK_SIZE = 100

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null

const isLink = (value: unknown): value is LinkStub =>
  isObject(value) && isObject(value.sys) && value.sys.type === "Link"

const isEntryLink = (value: unknown): value is LinkStub => isLink(value) && value.sys.linkType === "Entry"

// Only content links are ever unresolved; `sys.contentType`, `sys.space` etc. are link-shaped metadata and must survive.
const isContentLink = (value: unknown): value is LinkStub =>
  isLink(value) && (value.sys.linkType === "Entry" || value.sys.linkType === "Asset")

const childrenOf = (value: object): unknown[] =>
  Array.isArray(value) ? value : Object.entries(value).flatMap(([key, child]) => (key === "sys" ? [] : [child]))

// An always-false condition: hasAny with no attributes is never satisfied, so the gated content is hidden.
const toFailClosedCondition = (id: string) => ({
  sys: { id, type: "Entry", contentType: { sys: { type: "Link", linkType: "ContentType", id: "hasAny" } } },
  fields: { name: `Unresolved condition ${id} (failed closed)`, attributes: [] as string[] }
})

const collectUnresolvedConditions = (root: unknown): UnresolvedCondition[] => {
  const found: UnresolvedCondition[] = []
  const seen = new Set<object>()

  const walk = (value: unknown) => {
    if (!isObject(value) || seen.has(value)) return
    seen.add(value)

    if (!Array.isArray(value) && isObject(value.fields) && Array.isArray(value.fields.conditions)) {
      const conditions = value.fields.conditions
      conditions.forEach((condition, index) => {
        if (isEntryLink(condition)) {
          found.push({ conditions, index, id: condition.sys.id })
        } else if (
          isObject(condition) &&
          isSupportedCondition(condition as never) &&
          hasUnresolvedFieldLinks(condition)
        ) {
          found.push({ conditions, index, id: String((condition.sys as { id?: string }).id) })
        }
      })
    }

    childrenOf(value).forEach(walk)
  }

  walk(root)
  return found
}

const stripUnresolvedLinks = (root: unknown) => {
  const seen = new Set<object>()

  const walk = (value: unknown) => {
    if (!isObject(value) || seen.has(value)) return
    seen.add(value)

    if (Array.isArray(value)) {
      for (let i = value.length - 1; i >= 0; i--) {
        if (isContentLink(value[i])) value.splice(i, 1)
      }
    } else {
      for (const key of Object.keys(value)) {
        if (key !== "sys" && isContentLink(value[key])) delete value[key]
      }
    }

    childrenOf(value).forEach(walk)
  }

  walk(root)
}

// `toCondition` returns null for any other content type and the gate then drops it, which would leave the node ungated.
const SUPPORTED_CONDITION_TYPES: string[] = [
  ...Object.values(AttributeConditionType),
  ...Object.values(CountryConditionType)
]

const isSupportedCondition = (entry: { sys: { contentType?: { sys?: { id?: string } } } }): boolean =>
  SUPPORTED_CONDITION_TYPES.includes(entry.sys.contentType?.sys?.id ?? "")

// A fetched condition whose own attribute/country links didn't resolve would convert to an empty list, and an empty
// `mustHave`/`cantHave` passes. Such a condition can't be trusted, so it is treated as unresolved.
const hasUnresolvedFieldLinks = (entry: { fields?: unknown }): boolean => {
  const seen = new Set<object>()

  const walk = (value: unknown): boolean => {
    if (!isObject(value) || seen.has(value)) return false
    seen.add(value)
    return childrenOf(value).some((child) => isContentLink(child) || walk(child))
  }

  return walk(entry.fields)
}

const fetchInChunks = async (ids: string[], fetchEntriesByIds: FetchEntriesByIds) => {
  const chunks: string[][] = []
  for (let i = 0; i < ids.length; i += FETCH_CHUNK_SIZE) chunks.push(ids.slice(i, i + FETCH_CHUNK_SIZE))
  return (await Promise.all(chunks.map((chunk) => fetchEntriesByIds(chunk)))).flat()
}

export const resolveDeepConditions = async <T>(items: T, fetchEntriesByIds: FetchEntriesByIds): Promise<T> => {
  const unresolved = collectUnresolvedConditions(items)
  const failedClosed = new Set<string>()

  if (unresolved.length > 0) {
    const ids = [...new Set(unresolved.map(({ id }) => id))]
    const fetched = new Map(
      (await fetchInChunks(ids, fetchEntriesByIds))
        .filter((entry) => isSupportedCondition(entry) && !hasUnresolvedFieldLinks(entry))
        .map((entry) => [entry.sys.id, entry])
    )

    // Every shared reference to the same condition array gets the backfilled entry.
    for (const { conditions, index, id } of unresolved) {
      const entry = fetched.get(id)
      if (entry) {
        conditions[index] = entry
      } else {
        conditions[index] = toFailClosedCondition(id)
        failedClosed.add(id)
      }
    }
  }

  if (failedClosed.size > 0) {
    reportErrorMessage(
      `Contentful condition(s) could not be resolved; gated content was hidden (failed closed): ${[
        ...failedClosed
      ].join(", ")}`
    )
  }

  stripUnresolvedLinks(items)
  return items
}
